import { useMutation, useQueryClient } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import { parseErrorMessage } from '@/api/parse-error';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { ChronologyFact } from '@/types';

interface CreateChronologyParameters {
  chronologyId: string | null;
  factIds?: string[];
  titlePrefix?: string;
}

interface HookProps {
  matterId: string;
  subsetId: string;
  onSuccess: (data: CreateChronologyResponse) => void;
  onError: (error: Error) => void;
}

export interface CreateChronologyResponse {
  chronologyId: string;
  chronologyTitle: string;
  isNew: string;
  isProcessed: string;
  addedFacts?: ChronologyFact[];
}

export const useCreateChronology = ({ matterId, subsetId, onSuccess, onError }: HookProps) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  const createChronology = useMutation(
    async ({ chronologyId, factIds, titlePrefix }: CreateChronologyParameters) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          existingChronologyId: chronologyId,
          factIds,
          titlePrefix,
        },
      });
      const res = await fetch(`${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology`, fetchConfig);
      if (!res.ok) {
        throw new Error((await parseErrorMessage(res)) || 'Failed to create chronology');
      }
      return res.json(); // Return the response for use in onSuccess
    },
    {
      onSuccess: (data: CreateChronologyResponse) => {
        queryClient.invalidateQueries([QueryEntity.Chronologies, { subsetId }]);
        queryClient.invalidateQueries([QueryEntity.MatterChronologies]);
        onSuccess(data);
      },
      onError: (error: Error) => {
        onError(error);
      },
    },
  );

  return {
    createChronology,
  };
};
